import React, { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { addDays, subDays, format, isAfter } from "date-fns";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, ChevronUp, ChevronDown } from "lucide-react";

interface DateSelectorProps {
  initialDate?: Date;
  onDateChange?: (date: Date) => void;
  className?: string;
  maxDaysFromToday?: number;
  maxDate?: Date;
  isLoading?: boolean;
}

const DateSelector: React.FC<DateSelectorProps> = ({
  initialDate = new Date(),
  onDateChange,
  className,
  maxDaysFromToday,
  maxDate: explicitMaxDate,
  isLoading = false,
}) => {
  const validInitialDate = useMemo(() => {
    if (
      initialDate &&
      initialDate instanceof Date &&
      !isNaN(initialDate.getTime())
    ) {
      return initialDate;
    }
    return new Date();
  }, [initialDate]);

  const [date, setDate] = useState<Date | undefined>(validInitialDate);

  useEffect(() => {
    if (validInitialDate && validInitialDate.getTime() !== date?.getTime()) {
      setDate(validInitialDate);
    }
  }, [validInitialDate]);

  const maxDate = useMemo(() => {
    if (
      explicitMaxDate &&
      explicitMaxDate instanceof Date &&
      !isNaN(explicitMaxDate.getTime())
    ) {
      return explicitMaxDate;
    }

    if (maxDaysFromToday !== undefined) {
      const today = new Date();
      return addDays(today, maxDaysFromToday);
    }
    return undefined;
  }, [explicitMaxDate, maxDaysFromToday]);

  const isDateAllowed = (dateToCheck: Date): boolean => {
    if (maxDate && isAfter(dateToCheck, maxDate)) {
      return false;
    }
    return true;
  };

  const handleDateChange = (newDate: Date | undefined) => {
    if (newDate && isDateAllowed(newDate)) {
      setDate(newDate);
      if (onDateChange) {
        onDateChange(newDate);
      }
    }
  };

  const goToNextDay = () => {
    if (date) {
      const newDate = addDays(date, 1);
      if (isDateAllowed(newDate)) {
        handleDateChange(newDate);
      }
    }
  };

  const goToPreviousDay = () => {
    if (date) {
      const newDate = subDays(date, 1);
      handleDateChange(newDate);
    }
  };

  if (isLoading) {
    return (
      <div className={cn("flex items-center space-x-2", className)}>
        <Button
          variant="outline"
          size="sm"
          className="w-[240px] justify-start text-left font-normal"
          aria-label="Selected date loading"
          disabled
        >
          <CalendarIcon className="mr-2 h-4 w-4 animate-pulse" />
          <span className="h-4 w-24 bg-muted animate-pulse rounded" />
        </Button>
        <div className="flex flex-col border rounded-md overflow-hidden h-8 opacity-50">
          <Button
            variant="ghost"
            size="icon"
            className="h-1/2 w-6 rounded-none border-b px-1"
            aria-label="Next Day"
            title="Next Day"
            disabled
          >
            <ChevronUp className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-1/2 w-6 rounded-none px-1"
            aria-label="Previous Day"
            title="Previous Day"
            disabled
          >
            <ChevronDown className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex items-center space-x-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "w-[240px] justify-start text-left font-normal",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date && !isNaN(date.getTime()) ? (
              format(date, "PPP")
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateChange}
            initialFocus
            defaultMonth={date}
            disabled={(dateToCheck) => {
              if (maxDate && isAfter(dateToCheck, maxDate)) {
                return true;
              }
              return false;
            }}
          />
        </PopoverContent>
      </Popover>

      <div className="flex flex-col border rounded-md overflow-hidden h-8">
        <Button
          variant="ghost"
          size="icon"
          className="h-1/2 w-6 rounded-none border-b px-1"
          onClick={goToNextDay}
          aria-label="Next Day"
          title="Next Day"
          disabled={
            date && maxDate ? isAfter(addDays(date, 1), maxDate) : false
          }
        >
          <ChevronUp className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-1/2 w-6 rounded-none px-1"
          onClick={goToPreviousDay}
          aria-label="Previous Day"
          title="Previous Day"
        >
          <ChevronDown className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

export { DateSelector };
